/**
 * useUserMarketStats Hook
 * Fetches market insights data with scoping (view + filter + level) for user-facing analytics.
 * Uses the canonical scoping model from shared/analyticsScopes.ts.
 */

import { API_BASE_URL } from '@config/api';
import { useAuth } from '@contexts/AuthContext';
import type {
  AnalysisLevel,
  AnalyticsFilter,
  AnalyticsView,
  ApiScope,
} from '@parlay-picker/shared/analyticsScopes';
import {
  DEFAULT_FILTER_BY_VIEW,
  deriveApiScope,
  VALID_FILTERS_BY_VIEW,
} from '@parlay-picker/shared/analyticsScopes';
import { type ApiErrorResponse, getUserFriendlyError, safeParseJson } from '@utils/api';
import type { DailyMarketInsight } from '@views/lab/hooks/useLabDashboard';
import { useCallback, useEffect, useState } from 'react';

// =============================================================================
// Day Detail Types (mirrors backend UserDayDetail shape)
// =============================================================================

export interface UserDayDetailParlay {
  id: number;
  name: string | null;
  totalOdds: string | null;
  result: string;
  origins: string[];
  legs: Array<{
    player: string;
    prop: string;
    odds: string | null;
    hit: boolean | null;
    actualValue: string | null;
  }>;
}

export interface UserDayDetail {
  date: string;
  parlays: UserDayDetailParlay[];
  summary: {
    totalParlays: number;
    wins: number;
    losses: number;
    pending: number;
  };
}

// =============================================================================
// Hook Result
// =============================================================================

/** Time period preset value */
export type TimePeriod = '7d' | '14d' | '30d' | '60d' | 'all';

/** Map period values to day limits */
const PERIOD_TO_LIMIT: Record<TimePeriod, number> = {
  '7d': 7,
  '14d': 14,
  '30d': 30,
  '60d': 60,
  all: 365,
};

export interface UseUserMarketStatsResult {
  /** Daily market insight data for the current scope */
  data: DailyMarketInsight[];
  loading: boolean;
  error: string | null;

  /** Current view (AI + Community vs My Stats) */
  view: AnalyticsView;
  setView: (view: AnalyticsView) => void;

  /** Current filter within the view */
  filter: AnalyticsFilter;
  setFilter: (filter: AnalyticsFilter) => void;

  /** Valid filters for the current view */
  validFilters: AnalyticsFilter[];

  /** Analysis level (legs vs parlays) — controls display, not data fetching */
  level: AnalysisLevel;
  setLevel: (level: AnalysisLevel) => void;

  /** Time period for data range */
  period: TimePeriod;
  setPeriod: (period: TimePeriod) => void;

  /** Day detail data (loaded on demand) */
  dayDetail: UserDayDetail | null;
  dayDetailLoading: boolean;
  fetchDayDetail: (date: string) => Promise<void>;
  clearDayDetail: () => void;

  /** When the server will next recalculate insights (ISO string, 3 AM ET daily) */
  nextRefreshAt: string | null;

  /** Refetch current scope data */
  refresh: () => void;
}

// =============================================================================
// Hook Implementation
// =============================================================================

export function useUserMarketStats(): UseUserMarketStatsResult {
  const { isAuthenticated, authFetch } = useAuth();

  // Scope state
  const [view, setViewState] = useState<AnalyticsView>('ai_community');
  const [filter, setFilter] = useState<AnalyticsFilter>('everything');
  const [level, setLevel] = useState<AnalysisLevel>('legs');
  const [period, setPeriod] = useState<TimePeriod>('30d');
  const limit = PERIOD_TO_LIMIT[period];

  // Data state
  const [data, setData] = useState<DailyMarketInsight[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Day detail state
  const [dayDetail, setDayDetail] = useState<UserDayDetail | null>(null);
  const [dayDetailLoading, setDayDetailLoading] = useState(false);

  // Server-provided next refresh time
  const [nextRefreshAt, setNextRefreshAt] = useState<string | null>(null);

  // When view changes, auto-select the default filter for that view
  const setView = useCallback((newView: AnalyticsView) => {
    setViewState(newView);
    setFilter(DEFAULT_FILTER_BY_VIEW[newView]);
  }, []);

  const validFilters = VALID_FILTERS_BY_VIEW[view];

  // Derive API scope from view + filter
  const scope: ApiScope = deriveApiScope(view, filter);

  // Fetch insights for the current scope
  const fetchInsights = useCallback(async () => {
    if (!isAuthenticated) {
      setError('Authentication required');
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const url = `${API_BASE_URL}/api/analytics/market-insights?scope=${scope}&limit=${limit}`;
      const response = await authFetch(url);
      const result = await safeParseJson<
        { insights: DailyMarketInsight[]; nextRefreshAt?: string } | ApiErrorResponse
      >(response);

      if (!response.ok) {
        const errorResult = result as ApiErrorResponse;
        throw new Error(getUserFriendlyError(errorResult.error));
      }

      const successResult = result as {
        insights: DailyMarketInsight[];
        nextRefreshAt?: string;
      };
      setData(successResult.insights);
      if (successResult.nextRefreshAt) {
        setNextRefreshAt(successResult.nextRefreshAt);
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch market insights';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated, authFetch, scope, limit]);

  // Fetch day detail for a specific date
  const fetchDayDetail = useCallback(
    async (date: string) => {
      if (!isAuthenticated) return;

      setDayDetailLoading(true);

      try {
        const url = `${API_BASE_URL}/api/analytics/day-detail?date=${date}`;
        const response = await authFetch(url);
        const result = await safeParseJson<{ dayDetail: UserDayDetail } | ApiErrorResponse>(
          response,
        );

        if (!response.ok) {
          const errorResult = result as ApiErrorResponse;
          throw new Error(getUserFriendlyError(errorResult.error));
        }

        setDayDetail((result as { dayDetail: UserDayDetail }).dayDetail);
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to fetch day detail';
        setError(errorMessage);
      } finally {
        setDayDetailLoading(false);
      }
    },
    [isAuthenticated, authFetch],
  );

  const clearDayDetail = useCallback(() => {
    setDayDetail(null);
  }, []);

  // Refetch when scope changes
  useEffect(() => {
    fetchInsights();
  }, [fetchInsights]);

  return {
    data,
    loading,
    error,
    view,
    setView,
    filter,
    setFilter,
    validFilters,
    level,
    setLevel,
    period,
    setPeriod,
    dayDetail,
    dayDetailLoading,
    fetchDayDetail,
    clearDayDetail,
    nextRefreshAt,
    refresh: fetchInsights,
  };
}
